import { DirectionType } from "@labci/bell-fg-widgets";
import { RefObject, useEffect, useRef } from "react";

const OVERSCROLL_THRESHOLD_X = 60;

type GestureState = {
  startX: number;
  startY: number;
  lastX: number;
  lastY: number;
  scrollElY: HTMLElement | null;
  scrollElX: HTMLElement | null;

  reachedTopAt: number | null;
  reachedBottomAt: number | null;
  reachedLeftAt: number | null;
  reachedRightAt: number | null;
};

const postToHost = (message: object) => {
  const payload = { type: "overscroll", ...message };
  const json = JSON.stringify(payload);
  if ((window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(json);
    return;
  }
  window.parent.postMessage(json, "*");
};

export const useOverscrollGesture = (containerRef: RefObject<HTMLElement>) => {
  const gestureRef = useRef<GestureState | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const findScrollable = (
      start: HTMLElement | null,
      axis: "y" | "x",
    ): HTMLElement | null => {
      let el = start;
      const stopAt = container.parentElement?.parentElement ?? null;
      while (el && el !== stopAt) {
        const style = getComputedStyle(el);
        const overflow = axis === "y" ? style.overflowY : style.overflowX;
        const hasScroll =
          axis === "y"
            ? el.scrollHeight > el.clientHeight
            : el.scrollWidth > el.clientWidth;
        if (/(auto|scroll|overlay)/.test(overflow) && hasScroll) return el;
        el = el.parentElement;
      }
      return null;
    };

    const getEdgeStateY = (el: HTMLElement | null) => {
      const scrollEl =
        el ??
        (document.scrollingElement as HTMLElement | null) ??
        document.documentElement;
      const scrollTop = scrollEl?.scrollTop ?? 0;
      const clientHeight = scrollEl?.clientHeight ?? 0;
      const scrollHeight = scrollEl?.scrollHeight ?? 0;
      return {
        scrollTop,
        clientHeight,
        scrollHeight,
        atTop: scrollTop <= 0,
        atBottom: Math.ceil(scrollTop + clientHeight) >= scrollHeight - 1,
      };
    };

    const getEdgeStateX = (el: HTMLElement | null) => {
      const scrollEl = el;
      const scrollLeft = scrollEl?.scrollLeft ?? 0;
      const clientWidth = scrollEl?.clientWidth ?? 0;
      const scrollWidth = scrollEl?.scrollWidth ?? 0;
      return {
        scrollLeft,
        clientWidth,
        scrollWidth,
        atLeft: !scrollEl || scrollLeft <= 0,
        atRight:
          !scrollEl || Math.ceil(scrollLeft + clientWidth) >= scrollWidth - 1,
      };
    };

    const onTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!touch) return;

      const target = e.target as HTMLElement;
      const scrollElY = findScrollable(target, "y");
      const scrollElX = findScrollable(target, "x");

      gestureRef.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        lastX: touch.clientX,
        lastY: touch.clientY,
        scrollElY,
        scrollElX,
        reachedTopAt: null,
        reachedBottomAt: null,
        reachedLeftAt: null,
        reachedRightAt: null,
      };
    };

    const onTouchMove = (e: TouchEvent) => {
      const g = gestureRef.current;
      const touch = e.touches[0];
      if (!g || !touch) return;

      g.lastX = touch.clientX;
      g.lastY = touch.clientY;

      const totalDy = touch.clientY - g.startY;
      const totalDx = touch.clientX - g.startX;
      const absDy = Math.abs(totalDy);
      const absDx = Math.abs(totalDx);

      if (absDy > absDx) {
        const edgeY = getEdgeStateY(g.scrollElY);

        if (edgeY.atTop && g.reachedTopAt === null) {
          g.reachedTopAt = touch.clientY;
        }

        if (!edgeY.atTop && g.reachedTopAt !== null) {
          g.reachedTopAt = null;
        }

        if (edgeY.atBottom && g.reachedBottomAt === null) {
          g.reachedBottomAt = touch.clientY;
        }
        if (!edgeY.atBottom && g.reachedBottomAt !== null) {
          g.reachedBottomAt = null;
        }

        if (g.reachedTopAt !== null) {
          const overDy = touch.clientY - g.reachedTopAt;
          if (overDy > 0) {
            postToHost({
              direction: DirectionType.Top,
              delta: Math.abs(overDy),
            });
          }
        } else if (g.reachedBottomAt !== null) {
          const overDy = touch.clientY - g.reachedBottomAt;
          if (overDy < 0) {
            postToHost({
              direction: DirectionType.Bottom,
              delta: Math.abs(overDy),
            });
          }
        }
      } else if (absDx > absDy) {
        const edgeX = getEdgeStateX(g.scrollElX);

        if (edgeX.atLeft && g.reachedLeftAt === null) {
          g.reachedLeftAt = touch.clientX;
        }
        if (!edgeX.atLeft && g.reachedLeftAt !== null) {
          g.reachedLeftAt = null;
        }

        if (edgeX.atRight && g.reachedRightAt === null) {
          g.reachedRightAt = touch.clientX;
        }
        if (!edgeX.atRight && g.reachedRightAt !== null) {
          g.reachedRightAt = null;
        }
      }
    };

    const onTouchEnd = () => {
      const g = gestureRef.current;
      if (!g) return;
      gestureRef.current = null;

      const edgeX = getEdgeStateX(g.scrollElX);

      if (g.reachedLeftAt !== null && edgeX.atLeft) {
        const overDx = g.lastX - g.reachedLeftAt;
        if (overDx >= OVERSCROLL_THRESHOLD_X) {
          postToHost({
            direction: DirectionType.Left,
            delta: Math.abs(overDx),
          });
          return;
        }
      }

      if (g.reachedRightAt !== null && edgeX.atRight) {
        const overDx = g.lastX - g.reachedRightAt;
        if (-overDx >= OVERSCROLL_THRESHOLD_X) {
          postToHost({
            direction: DirectionType.Right,
            delta: Math.abs(overDx),
          });
          return;
        }
      }
    };

    const wheelAccumX = { value: 0 };
    const wheelAccumY = { value: 0 };
    let wheelResetTimer: ReturnType<typeof setTimeout> | null = null;

    const onWheel = (e: WheelEvent) => {
      const target = e.target as HTMLElement;

      wheelAccumY.value += e.deltaY;
      wheelAccumX.value += e.deltaX;

      if (wheelResetTimer) clearTimeout(wheelResetTimer);
      wheelResetTimer = setTimeout(() => {
        const absY = Math.abs(wheelAccumY.value);
        const absX = Math.abs(wheelAccumX.value);

        if (absX > absY && absX >= OVERSCROLL_THRESHOLD_X) {
          const edgeX = getEdgeStateX(findScrollable(target, "x"));
          if (wheelAccumX.value < 0 && edgeX.atLeft) {
            postToHost({
              direction: DirectionType.Left,
              delta: Math.abs(wheelAccumX.value),
            });
          } else if (wheelAccumX.value > 0 && edgeX.atRight) {
            postToHost({
              direction: DirectionType.Right,
              delta: Math.abs(wheelAccumX.value),
            });
          }
        }

        wheelAccumX.value = 0;
        wheelAccumY.value = 0;
      }, 150);
    };

    container.addEventListener("wheel", onWheel, { passive: true });
    container.addEventListener("touchstart", onTouchStart, { passive: true });
    container.addEventListener("touchmove", onTouchMove, { passive: true });
    container.addEventListener("touchend", onTouchEnd, { passive: true });

    return () => {
      if (wheelResetTimer) clearTimeout(wheelResetTimer);
      container.removeEventListener("wheel", onWheel);
      container.removeEventListener("touchstart", onTouchStart);
      container.removeEventListener("touchmove", onTouchMove);
      container.removeEventListener("touchend", onTouchEnd);
    };
  }, [containerRef]);
};
